import { useEffect, useRef } from "react";

type Options = {
    /** When false, scroll listener is idle (e.g. loading / fully fetched). */
    enabled: boolean;
    onLoadMore: () => void;
    /** Pixels from bottom that count as "reached end". */
    offset?: number;
};

function hasReachedBottom(offset: number): boolean {
    const scrollingElement =
        document.scrollingElement ?? document.documentElement;
    const scrollTop =
        window.scrollY ||
        scrollingElement.scrollTop ||
        document.body.scrollTop ||
        0;
    const scrollHeight = Math.max(
        scrollingElement.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.scrollHeight
    );

    return window.innerHeight + scrollTop >= scrollHeight - offset;
}

/**
 * Window/document infinite scroll. Re-checks when `enabled` becomes true so
 * loads continue if the user is already at the bottom after a page append.
 */
export function useWindowInfiniteScroll({
    enabled,
    onLoadMore,
    offset = 120,
}: Options) {
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const maybeLoadMore = () => {
            if (hasReachedBottom(offset)) {
                onLoadMoreRef.current();
            }
        };

        window.addEventListener("scroll", maybeLoadMore, { passive: true });
        window.addEventListener("resize", maybeLoadMore);
        maybeLoadMore();

        return () => {
            window.removeEventListener("scroll", maybeLoadMore);
            window.removeEventListener("resize", maybeLoadMore);
        };
    }, [enabled, offset]);
}
